import { useEffect, useRef, useState } from 'react';

// material-ui
import { Box, Fab, Grow, IconButton, InputAdornment, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

// project imports
import useAuth from 'hooks/useAuth';
import { useDispatch, useSelector } from 'store';
import { askQuestion, getAskJivanEnabled, resetAskJivanState, setOpen, setView, startNewConversation } from 'store/slices/askJivan';
import ConversationHistory from './conversationHistory';
import JivanFace from './jivanFace';
import MessageList from './messageList';

// assets
import { IconChevronDown, IconHistory, IconMessagePlus, IconSend, IconX } from '@tabler/icons';

// Intercom-style floating panel: sized to a conversation, not to the monitor, so the
// page stays visible behind it. Full screen under `sm`.
const PANEL_WIDTH = 400;
const PANEL_HEIGHT = 'min(640px, calc(100vh - 120px))';
// clears the 56px FAB plus its 24px margin
const PANEL_BOTTOM = 88;
const MAX_QUESTION_LENGTH = 2000;
// the header and composer are white lids over the tinted thread; a hairline shadow on the
// edge facing the thread marks them instead of a divider
const lidEdgeColor = (theme) => alpha(theme.palette.primary[800], 0.08);
const headerEdge = (theme) => `0 1px 0 ${lidEdgeColor(theme)}`;
const composerEdge = (theme) => `0 -1px 0 ${lidEdgeColor(theme)}`;

// Floating Ask Jivan chat. Lives in MainLayout so the open panel and the thread
// survive route changes. Hidden unless platform admin enabled it for the org.
const AskJivanWidget = () => {
    const dispatch = useDispatch();
    const { organizationMeta } = useAuth();
    const orgId = organizationMeta?.meta?.id;
    const { enabled, open, view, conversationId, messages, pending, loadingConversation } = useSelector((state) => state.askJivan);
    const [question, setQuestion] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (!orgId) {
            return;
        }
        // a thread belongs to one org: start clean when the org changes
        resetAskJivanState();
        dispatch(getAskJivanEnabled());
    }, [dispatch, orgId]);

    // the input is disabled while a question is out, which drops its focus: hand it back
    // for the follow-up, unless the user has since moved focus somewhere else on the page
    useEffect(() => {
        if (!pending && document.activeElement === document.body) {
            inputRef.current?.focus();
        }
    }, [pending]);

    if (!enabled) {
        return null;
    }

    const canSend = question.trim() !== '' && !pending;

    const send = async () => {
        if (!canSend) {
            return;
        }
        const asked = question.trim();
        // the question moves into the thread straight away; it comes back to the input to retry if it fails
        setQuestion('');
        const answered = await dispatch(askQuestion(conversationId, asked));
        if (!answered) {
            setQuestion(asked);
        }
    };

    const onKeyDown = (event) => {
        // Enter sends; Shift+Enter adds a line
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
        }
    };

    return (
        <>
            {/* the launcher stays put and doubles as the minimise control while the panel is open */}
            <Tooltip title={open ? 'Minimise' : 'Ask Jivan'} placement="top">
                <Fab
                    color="secondary"
                    aria-label={open ? 'Minimise Ask Jivan' : 'Ask Jivan'}
                    data-testid="ask-jivan-fab"
                    onClick={() => dispatch(setOpen(!open))}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: (theme) => theme.zIndex.drawer + 3,
                        // the full-screen panel covers the launcher on phones; the header X closes there
                        display: { xs: open ? 'none' : 'inline-flex', sm: 'inline-flex' }
                    }}
                >
                    {open ? <IconChevronDown size={28} /> : <JivanFace size={44} />}
                </Fab>
            </Tooltip>
            <Grow in={open} style={{ transformOrigin: 'bottom right' }} unmountOnExit>
                <Paper
                    elevation={8}
                    data-testid="ask-jivan-drawer"
                    sx={{
                        position: 'fixed',
                        right: { xs: 0, sm: 24 },
                        bottom: { xs: 0, sm: PANEL_BOTTOM },
                        width: { xs: '100%', sm: PANEL_WIDTH },
                        height: { xs: '100%', sm: PANEL_HEIGHT },
                        borderRadius: { xs: 0, sm: 3 },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        zIndex: (theme) => theme.zIndex.drawer + 2
                    }}
                >
                    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.5, boxShadow: headerEdge, zIndex: 1 }}>
                        <JivanFace size={32} />
                        <Typography variant="h4" sx={{ flex: 1 }}>
                            Ask Jivan
                        </Typography>
                        <Tooltip title="New conversation" placement="top">
                            {/* span keeps the tooltip working while the button is disabled */}
                            <span>
                                <IconButton
                                    size="small"
                                    // leaving the thread mid-question would strand the answer
                                    disabled={pending}
                                    onClick={() => dispatch(startNewConversation())}
                                    data-testid="ask-jivan-new"
                                >
                                    <IconMessagePlus size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={view === 'history' ? 'Back to chat' : 'Past conversations'} placement="top">
                            <span>
                                <IconButton
                                    size="small"
                                    color={view === 'history' ? 'primary' : 'default'}
                                    // asking in the old thread while a past one loads would strand the question
                                    disabled={loadingConversation}
                                    onClick={() => dispatch(setView(view === 'history' ? 'chat' : 'history'))}
                                    data-testid="ask-jivan-history-toggle"
                                >
                                    <IconHistory size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Close" placement="top">
                            <IconButton size="small" onClick={() => dispatch(setOpen(false))} data-testid="ask-jivan-close">
                                <IconX size={18} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    {view === 'history' && <ConversationHistory />}
                    {view === 'chat' && (
                        <>
                            <MessageList messages={messages} pending={pending} />
                            <Box sx={{ p: 1.5, boxShadow: composerEdge, zIndex: 1 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    size="small"
                                    placeholder="Ask a question"
                                    value={question}
                                    onChange={(event) => setQuestion(event.target.value)}
                                    onKeyDown={onKeyDown}
                                    disabled={pending}
                                    inputRef={inputRef}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: (theme) => alpha(theme.palette.primary[800], 0.18)
                                        }
                                    }}
                                    inputProps={{ maxLength: MAX_QUESTION_LENGTH, 'data-testid': 'ask-jivan-input' }}
                                    // eslint-disable-next-line react/jsx-no-duplicate-props
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    disabled={!canSend}
                                                    onClick={send}
                                                    data-testid="ask-jivan-send"
                                                    aria-label="Send"
                                                >
                                                    <IconSend size={18} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </Paper>
            </Grow>
        </>
    );
};

export default AskJivanWidget;
